import React from 'react';
import { ExclamationMarkCircle24 } from '@honeybook/icons';
import HBBox from '../HBBox/HBBox';
import HBButton from '../HBButton/HBButton';
import HBFlex from '../HBFlex/HBFlex';
import HBText from '../HBText/HBText';
import { GraphicWrapper } from './HBErrorScreen.styles';
import { Z_INDEX } from '../../styles/variables';
import { vars } from '../../theme';
import type { HBErrorScreenProps } from './HBErrorScreen.types';

export const HB_ERROR_SCREEN_TEST_ID = 'hb-error-screen';

const defaultGraphic = (
    <ExclamationMarkCircle24 color={vars.colors.base.danger} />
);

/**
 * Full-viewport error page: a centered graphic, heading, message, optional extra
 * content, and a single action. Use it when the whole surface has failed and the
 * user cannot continue. For an error or empty state inside a panel, card, or
 * table, use `HBEmptyState` instead.
 */
const HBErrorScreen = ({
    children,
    graphic = defaultGraphic,
    message,
    primaryAction,
    testId = HB_ERROR_SCREEN_TEST_ID,
    title
}: HBErrorScreenProps) => (
    <HBBox
        displayName="ErrorScreenOverlay"
        position="fixed"
        top="0"
        right="0"
        bottom="0"
        left="0"
        zIndex={String(Z_INDEX.FULL_SCREEN)}
        backgroundColor="surface.container"
        data-testid={testId}
        role="alert"
    >
        <HBFlex
            displayName="ErrorScreenContent"
            direction="column"
            align="center"
            justify="center"
            gap="32"
            height="100%"
            paddingX="24"
        >
            {graphic && (
                <GraphicWrapper displayName="ErrorScreenGraphic" display="flex">
                    {graphic}
                </GraphicWrapper>
            )}

            <HBFlex
                displayName="ErrorScreenText"
                direction="column"
                align="center"
                gap="16"
                maxWidth="650px"
            >
                <HBText
                    type="display40"
                    tag="h1"
                    align="center"
                    testId={`${HB_ERROR_SCREEN_TEST_ID}--title`}
                >
                    {title}
                </HBText>

                {message && (
                    <HBText
                        type="body24"
                        color="text.secondary"
                        align="center"
                        testId={`${HB_ERROR_SCREEN_TEST_ID}--message`}
                    >
                        {message}
                    </HBText>
                )}

                {children}
            </HBFlex>

            {primaryAction && (
                <HBButton
                    size="lg"
                    onClick={primaryAction.onClick}
                    testId={`${HB_ERROR_SCREEN_TEST_ID}--action`}
                >
                    {primaryAction.label}
                </HBButton>
            )}
        </HBFlex>
    </HBBox>
);

export default HBErrorScreen;
